import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Flex,
  Heading,
  Link,
  Spinner,
  Table,
  Tag,
  Text,
  VStack,
} from '@chakra-ui/react';
import { LuExternalLink } from 'react-icons/lu';
import { useParams } from 'react-router-dom';
import axios from 'axios';

function InsiderProfile() {
  const { cik } = useParams();
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const apiBaseUrl = useMemo(
    () => (process.env.REACT_APP_API_URL || '').replace(/\/+$/, ''),
    []
  );

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    setError('');
    setProfile(null);

    axios
      .get(`${apiBaseUrl}/api/sec-insiders/${encodeURIComponent(cik || '')}`, {
        signal: controller.signal,
      })
      .then(({ data }) => setProfile(data))
      .catch((err) => {
        if (axios.isCancel(err) || err?.name === 'CanceledError') return;
        setError(
          err?.response?.data?.error ||
            err?.message ||
            'Unable to load this SEC insider profile.'
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });

    return () => controller.abort();
  }, [apiBaseUrl, cik]);

  if (isLoading) {
    return (
      <Flex minH="50vh" align="center" justify="center" gap={3}>
        <Spinner />
        <Text>Loading SEC insider profile…</Text>
      </Flex>
    );
  }

  if (error) {
    return (
      <Box maxW="900px" mx="auto" p={{ base: 5, md: 8 }}>
        <Alert.Root status="error">
          <Alert.Indicator />
          <Alert.Content>{error}</Alert.Content>
        </Alert.Root>
      </Box>
    );
  }

  const filings = Array.isArray(profile?.filings) ? profile.filings : [];
  return (
    <Box maxW="1100px" mx="auto" p={{ base: 5, md: 8 }}>
      <VStack align="stretch" gap={6}>
        <Box>
          <Flex align="center" gap={3} wrap="wrap">
            <Heading as="h1" size="2xl">{profile?.name || 'SEC insider'}</Heading>
            {profile?.has_owner_transactions && (
              <Tag.Root colorPalette="blue" variant="subtle">
                <Tag.Label>Reporting owner</Tag.Label>
              </Tag.Root>
            )}
          </Flex>
          <Flex mt={2} gap={3} align="center" wrap="wrap" color="gray.600">
            <Text>CIK {profile?.cik}</Text>
            {profile?.sec_name && profile.sec_name !== profile.name && (
              <Text>SEC name: {profile.sec_name}</Text>
            )}
            {profile?.sec_profile_url && (
              <Link href={profile.sec_profile_url} target="_blank" rel="noopener noreferrer">
                <Flex as="span" align="center" gap={1}>
                  SEC submissions data <LuExternalLink aria-hidden />
                </Flex>
              </Link>
            )}
          </Flex>
        </Box>

        <Box borderWidth="1px" borderRadius="lg" overflow="hidden">
          <Box px={5} py={4} borderBottomWidth="1px">
            <Heading as="h2" size="md">Recent ownership filings</Heading>
            <Text mt={1} color="gray.600" fontSize="sm">
              Forms 3, 4, and 5 reported under this person’s SEC CIK.
            </Text>
          </Box>
          {filings.length ? (
            <Box overflowX="auto">
              <Table.Root size="sm">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>Form</Table.ColumnHeader>
                    <Table.ColumnHeader>Filing date</Table.ColumnHeader>
                    <Table.ColumnHeader>Reporting date</Table.ColumnHeader>
                    <Table.ColumnHeader>Accession number</Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {filings.map((filing) => (
                    <Table.Row key={filing.accession_number}>
                      <Table.Cell>
                        {filing.url ? (
                          <Link
                            href={filing.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            color="blue.600"
                            fontWeight="semibold"
                          >
                            <Flex as="span" align="center" gap={1}>
                              {filing.form_type} <LuExternalLink aria-hidden />
                            </Flex>
                          </Link>
                        ) : filing.form_type}
                      </Table.Cell>
                      <Table.Cell>{filing.filing_date || '—'}</Table.Cell>
                      <Table.Cell>{filing.report_date || '—'}</Table.Cell>
                      <Table.Cell fontFamily="mono" fontSize="xs">
                        {filing.accession_number}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>
          ) : (
            <Text p={5} color="gray.600">No recent ownership filings were returned by the SEC.</Text>
          )}
        </Box>
      </VStack>
    </Box>
  );
}

export default InsiderProfile;
